import React, { useState } from 'react';
import { useQueryClient } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import { Switch } from 'antd';
import styled from 'styled-components';

import { setCurrentView } from 'apis/UserAPI';
import { COMING_SOON_FEATURE, COMING_SOON_ROUTE } from 'components/ComingSoonFeatures/constants';
import { IMPERSONATION_VIEW, ImpersonationViewType } from 'constants/impersonation';
import { canAccessAISpeedToLead, canAccessComingSoonFeatures, isSupportingUser } from 'helpers/permissions';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { handleSwitchCurrentView } from 'store/slices/authSlice';

import { useRootContext } from '../RootContext';

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 0 14px 20px 14px;
`;

const Label = styled.span`
  font-size: 12px;
  color: var(--white-60);
`;

const SPEED_TO_LEAD_PATH = '/ai-competitive-intelligence/speed-to-lead';
const SPEED_TO_LEAD_PILOT_PATH = `${COMING_SOON_ROUTE}/${COMING_SOON_FEATURE.SPEED_TO_LEAD}`;

const SidebarViewAsSupportedUser = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { showMessage } = useRootContext();
  const currentUser = useAppSelector((state) => state.auth.currentUser);
  const [switching, setSwitching] = useState(false);

  if (!isSupportingUser(currentUser)) {
    return null;
  }

  const redirectForSpeedToLeadAccess = (nextView: ImpersonationViewType) => {
    // Swap between the real Speed to Lead page and its pilot (Coming Soon) page as access changes
    const nextUser = currentUser && { ...currentUser, current_view: nextView };
    const hasSpeedToLeadAccess = canAccessAISpeedToLead(nextUser);
    if (location.pathname.startsWith(SPEED_TO_LEAD_PATH) && !hasSpeedToLeadAccess) {
      const path = canAccessComingSoonFeatures(nextUser) ? SPEED_TO_LEAD_PILOT_PATH : '/ai-competitive-intelligence';
      navigate(`${path}${location.search}`, { replace: true });
    } else if (location.pathname === SPEED_TO_LEAD_PILOT_PATH && hasSpeedToLeadAccess) {
      navigate(`${SPEED_TO_LEAD_PATH}${location.search}`, { replace: true });
    }
  };

  const handleChange = async (checked: boolean) => {
    const view = checked ? IMPERSONATION_VIEW.SUPPORTED_USER : IMPERSONATION_VIEW.ADMIN;
    setSwitching(true);

    try {
      const response = await setCurrentView(view);

      // Drop requests and cached data of the previous view so admin-only content does not linger
      await queryClient.cancelQueries();
      dispatch(
        handleSwitchCurrentView({
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
          currentView: response.current_view,
        })
      );
      queryClient.resetQueries();

      redirectForSpeedToLeadAccess(response.current_view);
    } catch (error) {
      showMessage('error', 'Trouble switching the view!');
    } finally {
      setSwitching(false);
    }
  };

  return (
    <Container>
      <Label>View as supported user</Label>
      <Switch
        size="small"
        checked={currentUser?.current_view !== IMPERSONATION_VIEW.ADMIN}
        loading={switching}
        onChange={handleChange}
        data-test-id="sidebar-view-as-supported-user-switch"
      />
    </Container>
  );
};

export default SidebarViewAsSupportedUser;
